/**
 * main.ts
 * SPA Application Entry Point
 *
 * Initializes the router, layout, and all application services
 */

import { Router } from './router/Router';
import { routes } from './router/routes';
import { AppLayout } from './components/AppLayout';
import { SidebarLoader } from './ui/SidebarLoader';
import { DataService } from './services/DataService';
import { StorageService } from './services/StorageService';
import { ContentManager } from './components/ContentManager';
import { TimelineSelector } from './components/TimelineSelector';

/**
 * Main Application class for SPA
 */
class Application {
	private router: Router | null = null;
	private appLayout: AppLayout | null = null;
	private dataService: DataService;
	private storageService: StorageService;
	private sidebarLoader: SidebarLoader;
	private contentManager: ContentManager;
	private timelineSelector: TimelineSelector;

	constructor() {
		// Initialize services
		this.dataService = new DataService();
		this.storageService = new StorageService();
		this.sidebarLoader = new SidebarLoader();

		// Initialize content manager and timeline selector
		this.contentManager = new ContentManager(this.storageService);
		this.timelineSelector = new TimelineSelector(this.storageService);

		// Make ContentManager and TimelineSelector globally accessible for views
		interface WindowWithGlobals extends Window {
			__contentManager?: ContentManager;
			__timelineSelector?: TimelineSelector;
		}
		(window as WindowWithGlobals).__contentManager = this.contentManager;
		(window as WindowWithGlobals).__timelineSelector = this.timelineSelector;

		// Initialize application
		void this.init();
	}

	/**
	 * Initialize the SPA
	 */
	private async init(): Promise<void> {
		try {
			// Get app root element
			const appRoot = document.getElementById('app');
			if (!appRoot) {
				throw new Error('App root element #app not found');
			}

			// Initialize and render app layout
			this.appLayout = new AppLayout(this.sidebarLoader, this.storageService, routes);
			await this.appLayout.initialize(appRoot);

			// Get view container from layout
			const viewContainer = this.appLayout.getViewContainer();
			if (!viewContainer) {
				throw new Error('View container not found in layout');
			}

			// Initialize router with routes
			this.router = new Router({
				routes,
				mountElement: viewContainer,
				scrollToTop: true,
				scrollBehavior: 'smooth',

				// Global after navigation hook
				afterEach: (to, from): void => {
					// Use console.warn for navigation logging
					console.warn(`Navigated from ${(from?.path !== null && from?.path !== undefined) ? from.path : 'none'} to ${to.path}`);

					// Update sidebar active state
					if ((to.sidebarItem !== null && to.sidebarItem !== undefined && to.sidebarItem !== '') && this.appLayout !== null) {
						this.appLayout.updateActiveSidebar(to.sidebarItem);
					}

					// Reinitialize Lucide icons for new view
					if (typeof window.lucide !== 'undefined') {
						window.lucide.createIcons();
					}
				},
			});

			// Initialize ContentManager to set up event listeners
			this.contentManager.initialize();
		} catch (error) {
			console.error('❌ Failed to initialize application:', error);
			this.showError(error as Error);
		}
	}

	/**
	 * Show error message to user
	 */
	private showError(error: Error): void {
		const appRoot = document.getElementById('app');
		if (appRoot) {
			appRoot.innerHTML = `
				<div style="display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100vh; font-family: Arial, sans-serif; color: #d32f2f; text-align: center; padding: 20px;">
					<h1 style="font-size: 24px; margin-bottom: 10px;">Application Error</h1>
					<p style="color: #666; margin-bottom: 20px;">${error.message}</p>
					<button
						onclick="location.reload()"
						style="padding: 10px 20px; background: #1976d2; color: white; border: none; border-radius: 4px; cursor: pointer; font-size: 14px;"
					>
						Reload Page
					</button>
				</div>
			`;
		}
	}

	/**
	 * Get router instance
	 */
	public getRouter(): Router | null {
		return this.router;
	}

	/**
	 * Get data service
	 */
	public getDataService(): DataService {
		return this.dataService;
	}

	/**
	 * Get storage service
	 */
	public getStorageService(): StorageService {
		return this.storageService;
	}

	/**
	 * Get content manager
	 */
	public getContentManager(): ContentManager {
		return this.contentManager;
	}

	/**
	 * Get timeline selector
	 */
	public getTimelineSelector(): TimelineSelector {
		return this.timelineSelector;
	}
}

/**
 * Initialize application when DOM is ready
 */
document.addEventListener('DOMContentLoaded', () => {
	// Create application instance
	const app = new Application();

	// Make app available globally for debugging
	interface WindowWithApp extends Window {
		__app?: Application;
	}
	(window as WindowWithApp).__app = app;
});
